import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import moment from "jalali-moment";
import React, { useState } from "react";
import TableWallet from "../table";
import {
  numberSeparator,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";
import CardBoxLayout from "@/modules/merchant/layout/cardBoxLayout";
import StackKit from "@/shared/components/kit/stack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, TextField } from "@mui/material";
import CustomButton from "@/shared/components/kit/button";
import CustomSelect from "@/shared/components/common/select";

type TableBalanceProps = {
  data: any;
  setValue: any;
  value: any;
};
/**
 * TableBalance
 *
 * کامپوننت جدول تراکنش‌های انتقال موجودی.
 *
 * این کامپوننت لیستی از تراکنش‌های کاربر را در قالب جدول نمایش می‌دهد
 * و شامل امکاناتی مانند:
 * - جستجو در میان تراکنش‌ها
 * - فیلتر تعداد تراکنش‌های اخیر (۵، ۱۰، ۱۵، ۲۰)
 * - نمایش جزئیات تراکنش‌ها به‌صورت جدولی
 * - طراحی کاملاً ریسپانسیو برای نمایش در موبایل، تبلت و دسکتاپ
 *
 * ویژگی‌ها:
 * - استفاده از `CardBoxLayout` برای پس‌زمینه و استایل اصلی
 * - پشتیبانی از اسکرول افقی در موبایل برای جدول
 * - استفاده از `moment` جهت فرمت تاریخ شمسی
 * - نمایش آیکون رنگی بر اساس نوع تراکنش (واریز / برداشت)
 * - استفاده از `CustomSelect`، `CustomButton` و `TextField` برای کنترل‌ها
 *
 * کامپوننت‌های داخلی استفاده‌شده:
 * - StackKit
 * - TypoGraphyKit
 * - CustomSelect
 * - CustomButton
 * - TableWallet
 * - CardBoxLayout
 * - FileDownloadOutlinedIcon (آیکون وضعیت تراکنش)
 *
 * @component
 * @param {Object} props
 * @param {any[]} props.data - داده‌های تراکنش‌ها برای نمایش در جدول
 * @param {function} props.setValue - تابع تنظیم مقدار انتخاب‌شده برای تعداد تراکنش‌ها
 * @param {number} props.value - مقدار انتخاب‌شده فعلی از فیلتر تعداد تراکنش‌ها
 *
 * @example
 * ```tsx
 * const [value, setValue] = useState(10);
 * const data = [
 *   {
 *     amount: 500000,
 *     ts: 1696176000,
 *     ref_id: 123456,
 *     ref_type: "transfer",
 *     type: "واریز",
 *     source_type_title: "کیف پول",
 *     destination_type_title: "کاربر دیگر",
 *     description: "انتقال وجه تستی"
 *   }
 * ];
 *
 * <TableBalance data={data} setValue={setValue} value={value} />
 * ```
 */

const TableBalance = ({ data, setValue, value }: TableBalanceProps) => {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const handleChange = (val: any) => setValue(val);

  const refTypeMap: Record<string, string> = {
    transfer: "انتقال",
    order: "سفارش",
    "pro-ref-earn": "کارمزد چند سطحی حرفه ای",
    "ref-earn": "کارمزد تک سطحی",
    withdraw: "برداشت",
    installments: "اقساط",
    deposite: "افزایش موجودی",
    creditScoreFee: "کارمزد اعتبار سنجی",
    resellerOrder: "-",
    bistCard: "ساعی پی",
    edit: "ویرایش",
    "pos-pazirande": "دستگاه پوز",
    "pos-ghishe": "-",
    wallet: "کیف پول",
    ipg: "Ipg",
    "edit-pazirande": "اصلاح توسط پذیرنده",
    gift_fee: "-",
    emv: "خرید emv",
    qr_code: "QR Code",
  };

  const dataHeader = [
    { id: "amount", title: "مبلغ ( ریال)" },
    { id: "ts", title: "تاریخ و ساعت" },
    { id: "ref_id", title: "کدرهگیری تراکنش" },
    { id: "ref_type", title: "نوع" },
    { id: "source_type_title", title: "مبدا تراکنش" },
    { id: "destination_type_title", title: "مقصد تراکنش" },
    { id: "type", title: "وضعیت تراکنش" },
    { id: "description", title: "توضیحات" },
  ];

  const newData = data?.map((item: any, index: number) => ({
    ...item,
    ts: `${toPersianNumber(
      moment.unix(item.ts).locale("fa").format("HH:mm"),
    )} ${toPersianNumber(
      moment.unix(item.ts).locale("fa").format("jYYYY/jMM/jDD"),
    )}`,
    amount: (
      <TypoGraphyKit sx={{ direction: "ltr" }}>
        {numberSeparator(item.amount)}
      </TypoGraphyKit>
    ),
    to: toPersianNumber(item.to),
    from: item.from ? toPersianNumber(item.from) : "-",
    type: (
      <StackKit
        direction="row"
        sx={{ width: "100%", margin: "0 !important" }}
        alignItems="center"
        justifyContent="center"
        gap={1}
      >
        <FileDownloadOutlinedIcon
          style={{ color: item.type === "واریز" ? "green" : "red" }}
        />
        <TypoGraphyKit variant="body1" sx={{ fontSize: "14px" }}>
          {item.type}
        </TypoGraphyKit>
      </StackKit>
    ),
    ref_id: toPersianNumber(item.ref_id),
    ref_type: refTypeMap[item.ref_type] || item.ref_type,
  }));

  const fakeData = newData;

  // Handle page change from TableWallet
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle items per page change from TableWallet
  const handleItemsPerPageChange = (newSize: number) => {
    setSize(newSize);
    setPage(1); // Reset to first page when changing items per page
  };

  return (
    <CardBoxLayout
      sx={{
        justifyContent: "start",
        background: "#fff !important",
        overflow: "hidden",
        padding: "50px !important",
        borderRadius: "12px",

        alignItems: "center",
      }}
    >
      {/* Header */}
      <StackKit
        direction="column"
        sx={{ width: "100%", mb: "15px !important" }}
        gap={2}
        spacing={2}
      >
        <StackKit
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ width: "100%", gap: { xs: 2, md: 0 } }}
        >
          <TypoGraphyKit
            variant="h6"
            sx={{ fontSize: { xs: "16px", md: "18px" } }}
          >
            تراکنش‌های انتقال موجودی
          </TypoGraphyKit>
        </StackKit>
      </StackKit>

      {/* Responsive Table */}
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#555",
            borderRadius: "4px",
          },
        }}
      >
        <TableWallet
          dataHeader={dataHeader}
          dataTbody={fakeData || []}
          pagination={{
            current_page: page,
            per_page: size,
            total: data?.total || 0,
          }}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </Box>
    </CardBoxLayout>
  );
};

export default TableBalance;
